import { g } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import type { Shape } from '@hcie/shared';

type StyleSyncTarget = {
    isEditing: boolean;
    currentShape: Shape | null;
    updateStatusBarInfo: (shape: Shape) => void;
    refreshOverlayForExternalStyleChange: () => void;
};

function getNumber(v: any, fallback: number) {
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
}

function getBoolean(v: any) {
    return !!v;
}

function applyGlobalsToShapeStyle(shape: Shape) {
    if (!shape.style) (shape as any).style = {};
    const style: any = shape.style;

    style.width = getNumber((g as any).pen_width, style.width ?? 2);
    style.opacity = getNumber((g as any).pen_opacity, style.opacity ?? 1);
    style.hardness = getNumber((g as any).brush_hardness, style.hardness ?? 1);
    style.cornerRadius = getNumber((g as any).round_rect_corner_radius, style.cornerRadius ?? 10);

    style.fill = getBoolean((g as any).shape_fill);
    style.color = (g as any).pen_color ?? style.color ?? '#000';
    style.fillColor = (g as any).pen_secondary_color ?? style.fillColor;
}

function rerenderAfterStyleChange(target: StyleSyncTarget) {
    if (!target.currentShape) return;
    target.refreshOverlayForExternalStyleChange();
    target.updateStatusBarInfo(target.currentShape);
    EventBus.emit('RENDER_LAYERS', undefined);
}

export function bindVectorToolStyleSync(target: StyleSyncTarget) {
    const onFillChanged = (e: any) => {
        if (!target.isEditing || !target.currentShape) return;
        (g as any).shape_fill = !!e?.detail?.value;
        applyGlobalsToShapeStyle(target.currentShape);
        rerenderAfterStyleChange(target);
    };

    const onColorChanged = (e: any) => {
        if (!target.isEditing || !target.currentShape) return;
        const color = e?.detail?.color;
        if (typeof color === 'string') (g as any).pen_color = color;
        applyGlobalsToShapeStyle(target.currentShape);
        rerenderAfterStyleChange(target);
    };

    const onSecondaryColorChanged = (e: any) => {
        if (!target.isEditing || !target.currentShape) return;
        const color = e?.detail?.color;
        if (typeof color === 'string') (g as any).pen_secondary_color = color;
        applyGlobalsToShapeStyle(target.currentShape);
        rerenderAfterStyleChange(target);
    };

    const onPenWidthChanged = (e: any) => {
        if (!target.isEditing || !target.currentShape) return;
        const value = e?.detail?.value;
        (g as any).pen_width = getNumber(value, (g as any).pen_width ?? 2);
        applyGlobalsToShapeStyle(target.currentShape);
        rerenderAfterStyleChange(target);
    };

    const onOpacityChanged = (e: any) => {
        if (!target.isEditing || !target.currentShape) return;
        const value = e?.detail?.value;
        (g as any).pen_opacity = getNumber(value, (g as any).pen_opacity ?? 1);
        applyGlobalsToShapeStyle(target.currentShape);
        rerenderAfterStyleChange(target);
    };

    const onHardnessChanged = (e: any) => {
        if (!target.isEditing || !target.currentShape) return;
        const value = e?.detail?.value;
        (g as any).brush_hardness = getNumber(value, (g as any).brush_hardness ?? 1);
        applyGlobalsToShapeStyle(target.currentShape);
        rerenderAfterStyleChange(target);
    };

    const onCornerRadiusChanged = (e: any) => {
        if (!target.isEditing || !target.currentShape) return;
        const value = e?.detail?.value;
        (g as any).round_rect_corner_radius = getNumber(value, (g as any).round_rect_corner_radius ?? 10);
        applyGlobalsToShapeStyle(target.currentShape);
        rerenderAfterStyleChange(target);
    };

    window.addEventListener('fillChanged', onFillChanged as any);
    window.addEventListener('colorChanged', onColorChanged as any);
    window.addEventListener('secondaryColorChanged', onSecondaryColorChanged as any);
    window.addEventListener('penWidthChanged', onPenWidthChanged as any);
    window.addEventListener('syncOpacity', onOpacityChanged as any);
    window.addEventListener('hardnessChanged', onHardnessChanged as any);
    window.addEventListener('cornerRadiusChanged', onCornerRadiusChanged as any);

    return () => {
        window.removeEventListener('fillChanged', onFillChanged as any);
        window.removeEventListener('colorChanged', onColorChanged as any);
        window.removeEventListener('secondaryColorChanged', onSecondaryColorChanged as any);
        window.removeEventListener('penWidthChanged', onPenWidthChanged as any);
        window.removeEventListener('syncOpacity', onOpacityChanged as any);
        window.removeEventListener('hardnessChanged', onHardnessChanged as any);
        window.removeEventListener('cornerRadiusChanged', onCornerRadiusChanged as any);
    };
}
